import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BillingProvider } from "@/context/BillingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Navbar from "@/components/Navbar";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CampaignIntake from "@/pages/CampaignIntake";
import SocialGenerator from "@/pages/SocialGenerator";
import History from "@/pages/History";
import SavedPosts from "@/pages/SavedPosts";
import VoterList from "@/pages/VoterList";
import Admin from "@/pages/Admin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CalendarPage from "@/pages/Calendar";
import CampaignCalendarPage from "@/pages/CampaignCalendar";
import SpeechWriter from "@/pages/SpeechWriter";
import Advisor from "@/pages/Advisor";
import LaunchChecklist from "@/pages/LaunchChecklist";
import Graphics from "@/pages/Graphics";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import Volunteers from "@/pages/Volunteers";
import LawnSigns from "@/pages/LawnSigns";
import Finances from "@/pages/Finances";
import Communications from "@/pages/Communications";
import FieldData from "@/pages/FieldData";
import Planning from "@/pages/Planning";
import CallerMode, { CallerSettings } from "@/pages/CallerMode";
import LegalDocumentPage from "@/pages/LegalDocument";
import LegalGate from "@/components/LegalGate";
import ActivationGate from "@/components/ActivationGate";

function RootRedirect() {
  const { user, isCaller } = useAuth();
  if (user === null) return <div className="h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (user && isCaller) return <Navigate to="/caller" replace />;
  return user ? <Navigate to="/dashboard" replace /> : <Landing />;
}

/**
 * Router-level wall for caller accounts: if a caller lands on any route
 * other than /caller (or auth pages), bounce them back to /caller. No
 * navigation menu, back-to-dashboard link, or escape hatch exists in the
 * caller view, and this gate makes it impossible to reach the rest of the
 * app even by typing a URL.
 */
function CallerGate({ children }) {
  const { isCaller, user } = useAuth();
  const location = useLocation();
  const CALLER_ROUTES = ["/caller", "/caller/settings"];
  if (user && isCaller && !CALLER_ROUTES.includes(location.pathname)) {
    return <Navigate to="/caller" replace />;
  }
  return children;
}

import OnboardingTour from "@/components/OnboardingTour";
import AnalyticsHeartbeat from "@/components/AnalyticsHeartbeat";
import LegalFooter from "@/components/LegalFooter";

function AppShell({ children }) {
  return (
    <>
      <Navbar />
      <OnboardingTour />
      <AnalyticsHeartbeat />
      {children}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <BillingProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <CallerGate>
          <LegalGate>
          <ActivationGate>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/terms" element={<LegalDocumentPage kind="terms" />} />
            <Route path="/privacy" element={<LegalDocumentPage kind="privacy" />} />
            <Route
              path="/caller"
              element={
                <ProtectedRoute>
                  <CallerMode />
                </ProtectedRoute>
              }
            />
            <Route
              path="/caller/settings"
              element={
                <ProtectedRoute>
                  <CallerSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppShell><Dashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intake"
              element={
                <ProtectedRoute>
                  <AppShell><CampaignIntake /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/profile" element={<Navigate to="/settings" replace />} />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppShell><Settings /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <AppShell><Billing /></AppShell>
                </ProtectedRoute>
              }
            />
            {/* Category hub pages — navigation shells. Each tool inside has its
                own ProtectedRoute, so these hubs don't need to gate. */}
            <Route
              path="/communications"
              element={
                <ProtectedRoute>
                  <AppShell><Communications /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/field-data"
              element={
                <ProtectedRoute>
                  <AppShell><FieldData /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/planning"
              element={
                <ProtectedRoute>
                  <AppShell><Planning /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/generator"
              element={
                <ProtectedRoute requirePaid featureName="the Post Generator">
                  <AppShell><SocialGenerator /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/history"
              element={
                <ProtectedRoute requirePaid featureName="post history">
                  <AppShell><History /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/saved"
              element={
                <ProtectedRoute requirePaid featureName="Saved Posts">
                  <AppShell><SavedPosts /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/voters"
              element={
                <ProtectedRoute requirePro featureName="the Voter List">
                  <AppShell><VoterList /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/finances"
              element={
                <ProtectedRoute requirePro featureName="Finances">
                  <AppShell><Finances /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AppShell><Admin /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/graphics"
              element={
                <ProtectedRoute requirePaid featureName="Graphics">
                  <AppShell><Graphics /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="/calendar" element={<Navigate to="/scheduler" replace />} />
            <Route
              path="/scheduler"
              element={
                <ProtectedRoute requirePaid featureName="the Scheduler">
                  <AppShell><CalendarPage /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/events"
              element={
                <ProtectedRoute requirePaid featureName="the Campaign Calendar">
                  <AppShell><CampaignCalendarPage /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/speeches"
              element={
                <ProtectedRoute requirePaid featureName="the Speech Writer">
                  <AppShell><SpeechWriter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/advisor"
              element={
                <ProtectedRoute requirePaid featureName="the Campaign Advisor">
                  <AppShell><Advisor /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/launch-checklist"
              element={
                <ProtectedRoute requirePaid featureName="the Launch Checklist">
                  <AppShell><LaunchChecklist /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/volunteers"
              element={
                <ProtectedRoute requirePaid featureName="the Volunteer Database">
                  <AppShell><Volunteers /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/lawn-signs"
              element={
                <ProtectedRoute requirePaid featureName="the Lawn Sign Tracker">
                  <AppShell><LawnSigns /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ActivationGate>
          </LegalGate>
          </CallerGate>
          <LegalFooter />
        </BrowserRouter>
      </BillingProvider>
    </AuthProvider>
  );
}

export default App;
